import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AuthShell, AUTH_INPUT, AUTH_LABEL, AUTH_SUBMIT } from './AuthShell';

export const UpdatePasswordPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const { t } = useTranslation();

  const [form, setForm] = useState({
    password: '',
    confirmPassword: '',
  });

  // Detect if this is an invite flow - either explicit type=invite or if we have token_hash (from invite emails)
  const isInviteFlow = searchParams.get('type') === 'invite' ||
                       searchParams.has('token_hash') ||
                       searchParams.has('token');
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');

  useEffect(() => {
    // If we have tokens in the URL (from email link), set the session
    const setupSession = async () => {
      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) {
          console.error('Error setting session:', error);
          toast({
            title: t('invalidResetToken'),
            description: error.message,
            variant: 'destructive',
          });
          navigate('/auth');
        }
      }
    };

    setupSession();
  }, [accessToken, refreshToken, toast, t, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      toast({
        title: t('passwordMismatch'),
        description: t('passwordsMustMatch'),
        variant: 'destructive',
      });
      return;
    }

    if (form.password.length < 8) {
      toast({
        title: t('passwordTooShort'),
        description: t('passwordTooShort'),
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: form.password,
      });

      if (error) {
        toast({
          title: t('passwordResetFailed'),
          description: error.message,
          variant: 'destructive',
        });
      } else {
        setPasswordUpdated(true);
        toast({
          title: t('passwordSet'),
          description: t('passwordSetSuccess'),
        });

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: unknown) {
      console.error('Password update error:', error);
      toast({
        title: t('passwordResetFailed'),
        description: t('pleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (passwordUpdated) {
    return (
      <AuthShell>
        <div className="space-y-4 text-center">
          <div className="flex justify-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
              <CheckCircle className="h-7 w-7 text-white" />
            </span>
          </div>
          <div className="space-y-1.5">
            <h2 className="text-lg font-medium text-white">{t('passwordSet')}</h2>
            <p className="text-sm leading-relaxed text-white/60">{t('passwordSetSuccess')}</p>
            <p className="text-xs text-white/45">
              {t('youWillBeRedirectedToTheDashboardShortly')}
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5 text-center">
          <h2 className="text-lg font-medium text-white">
            {isInviteFlow ? t('setPassword') : t('resetPassword')}
          </h2>
          <p className="text-sm leading-relaxed text-white/60">
            {t('setPasswordDescription')}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className={AUTH_LABEL}>
            {isInviteFlow ? t('password') : t('newPassword')}
          </Label>
          <Input
            id="password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="new-password"
            required
            minLength={8}
            className={AUTH_INPUT}
          />
          <p className="text-xs text-white/45">{t('passwordTooShort')}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className={AUTH_LABEL}>{t('confirmPassword')}</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            autoComplete="new-password"
            required
            minLength={8}
            className={AUTH_INPUT}
          />
        </div>

        <Button type="submit" className={AUTH_SUBMIT} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('savePassword')}
        </Button>
      </form>
    </AuthShell>
  );
};
